import GameObject from '../../../GameObject/GameObject';
import { UIActions } from '../../../State/UI/UIActions';
import PopupBase from '../../../Prototype/UI/PopupBase';
import UIItem, { IImageData, ITextData } from '../../../Prototype/UI/UIItem';
import Game from '../../../Game';
import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import UITextComponent from '../../../Component/UI/Text';
import UIButton, { IUIButtonData } from '../../../Prototype/UI/BaseElements/UIButton';
import { IGameState } from '../../../State/Game/IGameState';
import { TextAlignment } from '../../../Prototype/UI/TextAlignment';
import { PopupID } from '../../../State/UI/PopupID';

export interface IBasePopupParams {
    underlay?: IImageData;
    title?: string;
    titleBg?: IImageData;
    buttons: IUIButtonData[];
    [key: string]: any;
}

export enum PopupMainButtonLocations {
    CENTER = 'center',
    LEFT = 'left',
    RIGHT = 'right',
}

export const MAIN_BUTTON_ANCHORS: {
    [key in PopupMainButtonLocations]: [number, number, number, number];
} = {
    [PopupMainButtonLocations.CENTER]: [0.5, 0.98, 0.5, 0.98],
    [PopupMainButtonLocations.RIGHT]: [0.7, 0.98, 0.7, 0.98],
    [PopupMainButtonLocations.LEFT]: [0.3, 0.98, 0.3, 0.98],
};

export default class BasePopupHandler extends BaseMenu {
    public audioClipShow: string;
    public audioClipHide: string;

    constructor(name: PopupID, uiSystem: BaseUISystem) {
        super(name, uiSystem, false);
    }
    protected onCreate() {
        this.createPopup();
    }
    public onShow(params: any): void {
        super.onShow(params);
    }
    public onShown(params?: any) {}
    public onHidden(params?: any) {}
    protected onClose(): void {
        this.closePopup();
    }
    protected onSubmit(): void {
        this.closePopup();
    }

    protected onSideMenuSubmit(): void {}

    protected handleContinueClick(): void {
        this.closePopup();
    }

    public handleStateChanged(state: IGameState): void {
        super.handleStateChanged(state);
    }

    protected closePopup(): void {
        UIActions.hidePopup(this.name);
    }
    protected createPopup(params?: IBasePopupParams): void {
        // Create layer for side menu to attach to
        const baseLayer: GameObject<PIXI.Container> = UIItem.createImage({
            ...this.backgroundParams,
            name: `baseLayer`,
            cacheKey: 'blank',
        });
        this.element.addChild(baseLayer);

        // Create background for popup
        const background: GameObject<PIXI.Container> = UIItem.createImage(this.backgroundParams);
        if (params.underlay) {
            //if(params.underlay.name)
            background.addChild(UIItem.createImage(params.underlay));
        }

        if (params.title !== null) {
            // Create title
            this.titleBgParams = params.titleBg ? params.titleBg : this.titleBgParams;
            this.titleParams = {
                ...this.titleParams,
                textLocalizationKey: params.title ? params.title : this.titleParams.textLocalizationKey,
            };

            let titleBg: GameObject<PIXI.Container> = undefined;
            if (params.title === 'Harvest') {
                titleBg = UIItem.createImage(this.harvestTitleParams);
            } else {
                titleBg = UIItem.createImage(this.titleBgParams);
            }

            titleBg.addChild(UIItem.createText(this.titleParams));
            background.addChild(titleBg);
        }

        if (params.buttons) {
            params.buttons.forEach((buttonData: IUIButtonData) => {
                background.addChild(GameObject.Instantiate(UIButton, buttonData));
            });
        }

        this.element.addChild(background);
    }

    protected set title(value: string) {
        this.background.findChildByName(this.titleParams.name).getComponent(UITextComponent).text = value;
    }
    protected get titleContainer(): GameObject<PIXI.Container> {
        return this.background.findChildByName(this.titleParams.name);
    }
    protected get submitBtn(): UIButton {
        return this.background.find(this.submitBtnParams.name) as UIButton;
    }
    protected get continueBtn(): UIButton {
        return this.background.find(this.continueBtnParams.name) as UIButton;
    }
    protected get closeBtn(): UIButton {
        return this.background.find(this.closeBtnParams.name) as UIButton;
    }
    protected get background(): GameObject<PIXI.Container> {
        return this.element.find(this.backgroundParams.name);
    }
    protected get popup(): PopupBase {
        return this.element.find('popup') as PopupBase;
    }
    protected get base(): GameObject<PIXI.Container> {
        return this.element.find('baseLayer');
    }
    protected getElementInitializeData() {
        return this.popupConstrains;
    }
    public onDestroy() {
        super.onDestroy();
    }

    protected submitBtnParams: IUIButtonData = {
        name: 'popup_submit_button',
        background: {
            name: 'submit_bg',
            layout: {
                anchors: MAIN_BUTTON_ANCHORS[PopupMainButtonLocations.CENTER],
                pivot: [0.5, 1],
                offsets: [0, -4, 0, -4],
                scale: [1, 1],
            },
            cacheKey: 'blank',
        },
        text: {
            name: 'submit_txt',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 6, 0, 6],
                scale: [1, 1],
            },
            textLocalizationKey: 'Continue',
            style: Game.style.getStyle('popup.green_btn'),
            align: TextAlignment.CENTER,
            bounds: [220, 130],
        },
        button: {
            name: 'submit_btn',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [-2, 50, -2, 50],
                scale: [1, 1],
            },
            cacheKey: 'button_medium_green',
        },
        buttonMode: true,
        onClick: this.onSubmit.bind(this),
    };

    protected continueBtnParams: IUIButtonData = {
        name: 'popup_continue_button',
        background: {
            name: 'continue_bg',
            layout: {
                anchors: MAIN_BUTTON_ANCHORS[PopupMainButtonLocations.CENTER],
                pivot: [0.5, 1],
                offsets: [-200, -4, -200, -4],
                scale: [1, 1],
            },
            cacheKey: 'blank',
        },
        text: {
            name: 'continue_txt',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 6, 0, 6],
                scale: [1, 1],
            },
            textLocalizationKey: 'Continue',
            style: Game.style.getStyle('popup.green_btn'),
            align: TextAlignment.CENTER,
            bounds: [220, 130],
        },
        button: {
            name: 'continue_btn',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [-2, 50, -2, 50],
                scale: [1, 1],
            },
            cacheKey: 'button_medium_green',
        },
        buttonMode: true,
        onClick: this.handleContinueClick.bind(this),
    };
    protected cancelBtnParams: IUIButtonData = {
        name: 'popup_cancel_button',
        background: {
            name: 'submit_bg',
            layout: {
                anchors: MAIN_BUTTON_ANCHORS[PopupMainButtonLocations.RIGHT],
                pivot: [0.5, 1],
                offsets: [0, -4, 0, -4],
                scale: [1, 1],
            },
            cacheKey: 'button_medium_bg',
        },
        text: {
            name: 'submit_txt',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
            },
            textLocalizationKey: 'Continue',
            style: Game.style.getStyle('popup.green_btn'),
            align: TextAlignment.CENTER,
            bounds: [220, 130],
        },
        button: {
            name: 'submit_btn',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [-2, 50, -2, 50],
                scale: [1, 1],
            },
            cacheKey: 'button_medium_green',
        },
        buttonMode: true,
        onClick: this.onSubmit.bind(this),
    };
    protected submitBtnBgParams: IImageData = {
        name: 'submit_background',
        layout: {
            anchors: [0.5, 0.88, 0.5, 0.88],
            pivot: [0.5, 0.5],
            offsets: [0, -7, 0, -7],
            scale: [1, 1],
        },
        cacheKey: 'blank',
    };
    protected underlayParams: IImageData = {
        name: 'underlay',
        cacheKey: '',
        layout: {
            anchors: [0.5, 0.54, 0.5, 0.54],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
    };

    protected backgroundParams: IImageData = {
        name: 'popup_background',
        layout: {
            anchors: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
            constrains: {
                heightMax: 744,
                heightMin: 744,
                widthMax: 858,
                widthMin: 858,
            },
        },
        cacheKey: 'popup_background',
    };
    protected titleParams: ITextData = {
        name: 'popup_title',
        text: 'Title',
        style: Game.style.getStyle('popup.title'),
        layout: {
            anchors: [0.5, 0.45, 0.5, 0.45],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        bounds: [460, 240],
    };
    protected harvestTitleParams: IImageData = {
        name: 'Harvest_title_background',
        layout: {
            anchors: [0.5, 0.01, 0.5, 0.01],
            pivot: [0.5, 0],
            offsets: [0, 20, 0, 20],
            scale: [1, 1],
        },
        cacheKey: 'popup_title_bg',
    };
    protected titleBgParams: IImageData = {
        name: 'title_background',
        layout: {
            anchors: [0.5, 0.01, 0.5, 0.01],
            pivot: [0.5, 0],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        cacheKey: 'popup_title_bg',
    };
    protected closeBtnParams: IUIButtonData = {
        name: 'popup_close_button',
        button: {
            name: 'close_button',
            layout: {
                anchors: [0.9, 0.15, 0.9, 0.15],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
            },
            cacheKey: 'button_close',
        },
        onClick: this.onClose.bind(this),
    };
    protected closeHarvestBtnParams: IUIButtonData = {
        name: 'popup_close_button',
        button: {
            name: 'close_button',
            layout: {
                anchors: [0.9, 0.15, 0.9, 0.15],
                pivot: [0.5, 0.5],
                offsets: [-45, 0, -45, 0],
                scale: [1, 1],
            },
            cacheKey: 'button_close',
        },
        onClick: this.onClose.bind(this),
    };
    protected popupConstrains: {
        heightMax: number;
        heightMin: number;
        widthMax: number;
        widthMin: number;
    } = {
        heightMax: 744,
        heightMin: 744,
        widthMax: 858,
        widthMin: 858,
    };
}
